import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';
import { vdjColor } from './lib/vdjLook.js';
import { extractContours, contourWorkerSource, CONTOUR_N, MAX_POINTS } from './lib/lightpaint-contours.js';

// Contrat du rig partagé : interdit. Les sources sont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'lightpaint',
  family: 'shader',
  danceability: 0.72,
  requiresTier: 0,
  energyLevel: 0.62,
  bpmMin: 95,
  bpmMax: 130,
  movement: 0.55,
  tonality: 0.70,
  prefers: { gridStrength: [0.5, 1.0] },
  enabled: true
};

// LIGHTPAINT : la pochette DESSINÉE EN LUMIÈRE, en pose longue. Deux points
// de lumière tracent les contours principaux de la pochette, extraits une
// fois par morceau dans un Web Worker (lib/lightpaint-contours.js) ; une
// troisième encadre le dessin. House, nu-disco, électro-pop, funk.
//
// LA POSE. Le tampon garde le tracé avec une DEMI-VIE de 10 mesures (16 au
// drop) : decay = 0,5^(dt / demi-vie), indépendante de la cadence. En HALF
// FLOAT quand le GPU sait y rendre ; sinon RGBA8 avec un arrondi
// stochastique (l'espérance est exacte). Un plancher linéaire de 0,004 par
// seconde finit les traînes (l'obturateur nettoie de toute façon chaque
// photo). Le Pi ne filtre pas les textures flottantes : en demi-résolution,
// l'affichage interpole à la main.
//
// Le tampon contient (couleur x exposition, exposition) : RGB = la couleur du
// trait multipliée par son exposition, A = l'exposition. L'affichage en tire
// le cœur blanc, la gaine et le halo (R1) en pleine résolution ; la couleur
// est RGB / A, donc chaque trait garde la sienne (couleur de la pochette le
// long du trait, ou couleur du style).
//
// LE TRACÉ. Les traits sont répartis entre deux sources par longueur
// cumulée équilibrée ; chaque source avance à VITESSE CONSTANTE le long de
// l'abscisse curviligne, si bien que ses traits finissent ensemble, et
// s'éteint entre deux traits (un huitième de temps de déplacement, rien
// n'est tracé). Chaque frame peint la portion parcourue depuis la frame
// précédente, en 8 sous-segments au plus, toujours DANS UN SEUL TRAIT : les
// points sont lus côté CPU dans le tableau du worker (le même que la
// « texture de données » du brief) et passés en uniforms, 9 par source. La
// lecture de texture dans le vertex shader n'est pas garantie en WebGL1, et
// le CPU doit de toute façon chercher l'abscisse dans la table des longueurs
// cumulées. Chaque sous-segment est une capsule (bouts ronds, distance par
// pixel), mélange MAX. La tête (point + étoile) est dessinée à l'écran,
// jamais dans le tampon.
//
// LA COMPOSITION. Un DIPTYQUE : deux dessins, chacun dans un carré de 62 %
// de la hauteur au plus, centrés verticalement, au milieu de l'espace libre
// entre la pochette et le bord, hors des zones d'exclusion (R6). Le droit
// est le miroir du gauche, une photo sur deux ils sont identiques. Une
// troisième source trace un cadre arrondi autour de chaque dessin, sur toute
// la photo. Écran trop étroit sur les côtés (portrait) : dessus/dessous.
//
// LE RYTHME. Temps : le trait 20 % plus épais pendant 120 ms. Mesure : rien.
// Phrase : la photo dure un nombre entier de phrases et au moins 8 mesures ;
// le dessin est tracé en 8 mesures (moins 3 temps de tenue quand la photo ne
// fait que 8 mesures), puis l'obturateur balaie l'écran pendant le dernier
// temps et une nouvelle photo commence dans un autre style : COULEURS (la
// couleur de la pochette le long de chaque trait), MONO (la couleur
// d'accent), NÉON (deux couleurs alternées). Drop : effacement, un seul grand
// dessin centré (90 % de la hauteur) derrière la pochette, tracé en 2
// mesures, demi-vie 16 mesures, puis retour au diptyque à la fin de la
// phrase. Sans BPM : un dessin en 20 s.
//
// REPLI : moins de 3 contours valides, pas de pochette, ou extraction en
// échec : deux rosaces géométriques avec précession, tracées de la même façon.
//
// DEBUG : ?lpcontours=1 (contours superposés, numérotés dans l'ordre de
// dessin), ?lpposter=1 (la pochette postérisée), ?lpstyle=color|mono|neon,
// ?lpdrop=1 (un drop toutes les 8 mesures), ?lpdecay=<mesures> (demi-vie),
// ?lpbuffer=rgba8, ?lpview=exposure, ?lpprobe=1 (window.__lightpaint).

const TAU = Math.PI * 2;
const DRAW_BEATS = 32;              // 8 mesures
const DROP_DRAW_BEATS = 8;          // 2 mesures (vitesse x4)
const MIN_PHOTO_BEATS = 32;
const HOLD_BEATS = 3;               // tenue minimale avant l'obturateur
const NO_GRID_DRAW_BEATS = 40;      // 20 s au tempo de repli
const NO_GRID_PHOTO_BEATS = 44;
const TRAVEL_BEATS = 1 / 8;
const HALF_LIFE_BARS = 10;
const HALF_LIFE_DROP_BARS = 16;
const LINEAR_FLOOR_PER_S = 0.004;
const BEAT_WIDEN = 0.20;
const PULSE_S = 0.12;
const NO_BPM = 120;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const COVER_TIMEOUT_S = 4;

// Le profil du trait, en pixels d'écran (R1). L'exposition écrite vaut 1 dans
// le cœur, SHEATH_E dans la gaine, SHEATH_E x 0,35 x exp(-d/10) au-delà.
const CORE_HALF_PX = 1.2;
const SHEATH_PX = 3.0;
export const SHEATH_E = 0.62;
const HALO_REACH_PX = 30;
const EDGE_PX = 1.5;
const WHITE_FROM = 0.66, WHITE_TO = 0.86;
const SHEATH_LUM = 0.70;
const WARM_WHITE = [1.0, 0.93, 0.82];
const W_MAX = 1.2 * (1 + BEAT_WIDEN);
const FRAME_W = 0.75;               // le cadre : un trait plus fin
const SPIKE_PX = 11;

// La composition, en unités de hauteur (y de -1 à 1).
const EDGE_MARGIN = 0.10;           // 5 % de la hauteur
const COVER_GAP = 0.05;
export const SQUARE_HALF = 0.62;    // carré de 62 % de la hauteur
export const DROP_HALF = 0.90;
export const FRAME_PAD = 0.06;      // le cadre, en fraction du demi-côté
const FRAME_RADIUS = 0.16;
const MIN_PANEL_HALF = 0.18;
export const EXCLUSIONS = [
  ['tr', 0.22, 0.12],               // Broadcast
  ['tl', 0.08, 0.12],               // micro
  ['br', 0.22, 0.10]                // navigation
];

const SUB = 8;                      // sous-segments par source et par frame, au plus
const NPTS = SUB + 1;
const MIN_BUFFER_H = 540;
const TIER = { 0: { scale: 0.5 }, 1: { scale: 0.5 }, 2: { scale: 1.0 } };
export const STYLES = ['color', 'mono', 'neon'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- le cadre ----------------------------------------------------------------
export function forbiddenOut(x, y, aspect, reach) {
  const A = aspect;
  if (Math.abs(x) > A - EDGE_MARGIN - reach || Math.abs(y) > 1 - EDGE_MARGIN - reach) return true;
  for (const [corner, fw, fh] of EXCLUSIONS) {
    const w = fw * 2 * A, h = fh * 2;
    const x0 = corner[1] === 'r' ? A - w : -A, x1 = corner[1] === 'r' ? A : -A + w;
    const y0 = corner[0] === 't' ? 1 - h : -1, y1 = corner[0] === 't' ? 1 : -1 + h;
    if (x > x0 - reach && x < x1 + reach && y > y0 - reach && y < y1 + reach) return true;
  }
  return false;
}
export function rectFits(cx, cy, hx, hy, aspect, reach) {
  const n = 48;
  for (let j = 0; j <= n; j++) {
    const fx = cx - hx + 2 * hx * j / n, fy = cy - hy + 2 * hy * j / n;
    if (forbiddenOut(fx, cy + hy, aspect, reach) || forbiddenOut(fx, cy - hy, aspect, reach) ||
        forbiddenOut(cx - hx, fy, aspect, reach) || forbiddenOut(cx + hx, fy, aspect, reach)) return false;
  }
  return true;
}
function shrinkToFit(h, centres, ext, aspect, reach) {
  const ok = (s) => centres.every((c) => rectFits(c[0], c[1], s * ext, s * ext, aspect, reach));
  if (ok(h)) return h;
  let lo = 0, hi = h;
  for (let it = 0; it < 24; it++) { const m = (lo + hi) / 2; if (ok(m)) lo = m; else hi = m; }
  return lo;
}

// The panels: centres and the drawing's half-side h (the drawing fills
// [-1, 1]² scaled by h; its frame reaches h x (1 + FRAME_PAD)).
export function layoutPanels(aspect, center, half, screenH, drop) {
  const unit = 2 / Math.max(1, screenH);
  const reach = (CORE_HALF_PX + SHEATH_PX) * W_MAX * unit;
  const cx = center[0], cy = center[1];
  if (drop) {
    const h = shrinkToFit(DROP_HALF, [[cx, 0]], 1, aspect, reach);
    return { mode: 'single', axis: 'x', centres: [[cx, 0]], h };
  }
  const ext = 1 + FRAME_PAD;
  const hasCover = half[0] > 0 && half[1] > 0;
  const bx = hasCover ? half[0] : 0, by = hasCover ? half[1] : 0;
  // side by side: each in the middle of the free space between the cover
  // (plus its gap) and the margin
  const rx0 = cx + bx + COVER_GAP, rx1 = aspect - EDGE_MARGIN;
  const lx0 = -aspect + EDGE_MARGIN, lx1 = cx - bx - COVER_GAP;
  const xr = (rx0 + rx1) / 2, xl = (lx0 + lx1) / 2;
  let hH = Math.min(SQUARE_HALF, (Math.min(rx1 - rx0, lx1 - lx0) / 2 - reach) / ext, (1 - EDGE_MARGIN - reach) / ext);
  if (hH > 0) hH = shrinkToFit(hH, [[xl, 0], [xr, 0]], ext, aspect, reach); else hH = 0;
  // above and below
  const ty0 = cy + by + COVER_GAP, ty1 = 1 - EDGE_MARGIN;
  const by0 = -1 + EDGE_MARGIN, by1 = cy - by - COVER_GAP;
  const yt = (ty0 + ty1) / 2, yb = (by0 + by1) / 2;
  let hV = Math.min(SQUARE_HALF, (Math.min(ty1 - ty0, by1 - by0) / 2 - reach) / ext, (aspect - EDGE_MARGIN - reach) / ext);
  if (hV > 0) hV = shrinkToFit(hV, [[cx, yb], [cx, yt]], ext, aspect, reach); else hV = 0;
  if (Math.max(hH, hV) < MIN_PANEL_HALF) {
    const h = shrinkToFit(SQUARE_HALF, [[cx, 0]], ext, aspect, reach);
    return { mode: 'single', axis: 'x', centres: [[cx, 0]], h };
  }
  if (hH >= hV) return { mode: 'diptych', axis: 'x', centres: [[xl, 0], [xr, 0]], h: hH };
  return { mode: 'diptych', axis: 'y', centres: [[cx, yt], [cx, yb]], h: hV };
}

// --- les données de tracé -------------------------------------------------------
// The geometric fallback: two roses with a continuous precession, in the
// same format as the worker's output (one stroke per source).
export function rosaceData() {
  const pts = new Float32Array(MAX_POINTS * 4), cum = new Float32Array(MAX_POINTS);
  const specs = [
    { k: 3, amp: 0.96, turns: 6, prec: 1 / 18, n: 1500 },
    { k: 5, amp: 0.56, turns: 4, prec: -1 / 20, n: 1200 }
  ];
  const strokes = [];
  let w = 0, run = 0;
  specs.forEach((S, si) => {
    const start = w;
    let len = 0, px = 0, py = 0;
    for (let j = 0; j < S.n; j++) {
      const th = S.turns * Math.PI * j / (S.n - 1);
      const r = S.amp * Math.cos(S.k * th), a = th * (1 + S.prec);
      const x = r * Math.cos(a), y = r * Math.sin(a);
      if (j > 0) { const d = Math.hypot(x - px, y - py); len += d; run += d; }
      pts[4 * w] = x; pts[4 * w + 1] = y; pts[4 * w + 2] = si; pts[4 * w + 3] = j === 0 ? 1 : 0;
      cum[w] = run; w++;
      px = x; py = y;
    }
    strokes.push({ src: si, order: si, start, count: S.n, len, closed: false, rgb: null });
  });
  return { n: CONTOUR_N, count: w, pts, cum, strokes, poster: null, thresholds: [], fallback: true, rosace: true };
}

// The frame: a rounded square of half-side 1 + FRAME_PAD, clockwise from the
// top-left corner's end, as one closed stroke.
export function frameData() {
  const E = 1 + FRAME_PAD, R = FRAME_RADIUS, a = E - R;
  const P = [];
  const arc = (cx, cy, a0) => { for (let j = 0; j <= 10; j++) { const t = a0 - (Math.PI / 2) * j / 10; P.push(cx + R * Math.cos(t), cy + R * Math.sin(t)); } };
  arc(a, a, Math.PI / 2);          // top-right corner
  arc(a, -a, 0);                   // bottom-right
  arc(-a, -a, -Math.PI / 2);       // bottom-left
  arc(-a, a, Math.PI);             // top-left
  P.push(P[0], P[1]);
  const n = P.length / 2;
  const pts = new Float32Array(n * 4), cum = new Float32Array(n);
  let run = 0;
  for (let j = 0; j < n; j++) {
    if (j > 0) run += Math.hypot(P[2 * j] - P[2 * j - 2], P[2 * j + 1] - P[2 * j - 1]);
    pts[4 * j] = P[2 * j]; pts[4 * j + 1] = P[2 * j + 1]; pts[4 * j + 3] = j === 0 ? 1 : 0;
    cum[j] = run;
  }
  return { pts, cum, strokes: [{ src: 2, order: 0, start: 0, count: n, len: run, closed: true, rgb: null }] };
}
const FRAME_DATA = frameData();

// The point at arc length s along a stroke (s from the stroke's start).
function segIndex(d, st, s) {
  const a = d.cum[st.start] + s;
  let lo = st.start, hi = st.start + st.count - 1;
  if (a <= d.cum[lo]) return lo;
  if (a >= d.cum[hi]) return hi - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (d.cum[m] <= a) lo = m; else hi = m; }
  return lo;
}
export function pointAt(d, st, s, out) {
  const i = segIndex(d, st, s);
  const a = d.cum[st.start] + s;
  const l = d.cum[i + 1] - d.cum[i];
  const f = l > 1e-9 ? clamp01((a - d.cum[i]) / l) : 0;
  out[0] = d.pts[4 * i] + (d.pts[4 * i + 4] - d.pts[4 * i]) * f;
  out[1] = d.pts[4 * i + 1] + (d.pts[4 * i + 5] - d.pts[4 * i + 1]) * f;
  return out;
}
// The polyline from s0 to s1 within one stroke, in at most SUB segments:
// the two ends and the stroke's own vertices between them (a stride when
// there are more than SUB - 1). Writes x, y pairs into out; returns the
// number of points.
export function strokePortion(d, st, s0, s1, out) {
  const i0 = segIndex(d, st, s0), i1 = segIndex(d, st, s1);
  const p = [0, 0];
  pointAt(d, st, s0, p); out[0] = p[0]; out[1] = p[1];
  let n = 1;
  const m = i1 - i0;                 // vertices strictly inside: i0+1 .. i1
  if (m > 0) {
    const take = Math.min(m, SUB - 1);
    for (let j = 0; j < take; j++) {
      const v = take === m ? i0 + 1 + j : i0 + 1 + Math.round(j * (m - 1) / (take - 1 || 1));
      out[2 * n] = d.pts[4 * v]; out[2 * n + 1] = d.pts[4 * v + 1]; n++;
    }
  }
  pointAt(d, st, s1, p); out[2 * n] = p[0]; out[2 * n + 1] = p[1]; n++;
  return n;
}

// One source's timeline: constant speed along the arc, lamp off for
// TRAVEL_BEATS between strokes, the last stroke ending at D beats.
export function scheduleSource(strokes, D) {
  const n = strokes.length;
  if (!n) return { strokes: [], speed: 0 };
  const L = strokes.reduce((a, s) => a + s.len, 0);
  const travel = n > 1 ? Math.min(TRAVEL_BEATS, 0.25 * D / (n - 1)) : 0;
  const speed = L / Math.max(1e-6, D - travel * (n - 1));
  let t = 0;
  const out = strokes.map((s) => {
    const o = Object.assign({}, s, { t0: t, t1: t + s.len / speed });
    t = o.t1 + travel;
    return o;
  });
  return { strokes: out, speed, travel };
}

// --- les couleurs -------------------------------------------------------------
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }
// Three palette colours, pairwise at least 0.25 apart in hue (R4); the first
// is the most saturated (the accent). Synthesised around it when missing.
export function styleColours(palette) {
  const hsl = { h: 0, s: 0, l: 0 };
  const cand = [];
  if (palette) for (const k of ['vividColors', 'dominantColors']) for (const c of (palette[k] || [])) {
    new THREE.Color(c).getHSL(hsl); cand.push({ c, h: hsl.h, s: hsl.s });
  }
  cand.sort((a, b) => b.s - a.s);
  const out = [];
  const h0 = cand.length ? cand[0].h : 0.83;
  if (cand.length) out.push(cand[0]);
  for (const k of cand) {
    if (out.length >= 3) break;
    if (out.every((x) => hueDist(x.h, k.h) >= 0.25)) out.push(k);
  }
  const synth = [h0 + 1 / 3, h0 + 2 / 3, h0];
  let q = 0;
  while (out.length < 3) {
    const h = ((synth[q++ % 3] % 1) + 1) % 1;
    if (out.every((x) => hueDist(x.h, h) >= 0.25) || q > 6) out.push({ c: new THREE.Color().setHSL(h, 1, 0.55).getHex(), h, s: 1 });
  }
  return out.map((x) => sheathColour(x.c, new THREE.Color()));
}
// vdjColor (saturation 0.85 at least), then at the sheath's luminance 0.70
// with no channel over 1 (R1)
export function sheathColour(c, out) {
  const o = vdjColor(c, out, 0.85);
  const lum = 0.2126 * o.r + 0.7152 * o.g + 0.0722 * o.b, mx = Math.max(o.r, o.g, o.b);
  return o.multiplyScalar(Math.min(1 / Math.max(mx, 1e-4), SHEATH_LUM / Math.max(lum, 1e-4)));
}
const _rgb = new THREE.Color();
export function strokeColour(style, stroke, cols, out) {
  if (style === 'color' && stroke.rgb) return sheathColour(_rgb.setRGB(stroke.rgb[0], stroke.rgb[1], stroke.rgb[2]), out);
  if (style === 'neon') return out.copy(cols[stroke.order % 2]);
  if (style === 'color') return out.copy(cols[stroke.src % 3]);     // the rosaces: one colour per source
  return out.copy(cols[0]);
}

// --- GLSL --------------------------------------------------------------------
const PANEL_GLSL = `
  uniform vec2 uPanelC[2];
  uniform float uPanelH;
  uniform vec2 uMirror;      // the second panel's mirror (x, y): 1 or -1
  uniform float uPanels;
  vec2 toScreen(vec2 q, float panel) {
    vec2 m = panel > 0.5 ? uMirror : vec2(1.0);
    vec2 c = panel > 0.5 ? uPanelC[1] : uPanelC[0];
    return c + uPanelH * q * m;
  }
`;

const segmentVertexShader = `
  precision highp float;
  ${PANEL_GLSL}
  attribute float aSrc;
  attribute float aPanel;
  attribute float aSub;
  uniform vec2 uPts[${3 * NPTS}];
  uniform float uN[3];       // segments to draw this frame, per source
  uniform vec3 uCol[3];
  uniform float uW[3];
  uniform float uAspect;
  uniform float uPxPerUnit;
  uniform float uBufPx;
  varying vec2 vRel;
  varying vec2 vSeg;
  varying float vHc;
  varying float vHs;
  varying float vGain;
  varying float vAa;
  varying vec3 vCol;
  void main() {
    int i = int(aSrc + 0.5);
    int j = int(aSub + 0.5);
    float n = i == 0 ? uN[0] : (i == 1 ? uN[1] : uN[2]);
    if (aSub >= n || aPanel >= uPanels) { gl_Position = vec4(0.0); return; }
    float lowRes = step(1.01, uBufPx);
    vAa = 0.5 * ${EDGE_PX.toFixed(2)} * max(1.0, uBufPx);
    vec2 a = toScreen(uPts[i * ${NPTS} + j], aPanel) * uPxPerUnit;
    vec2 b = toScreen(uPts[i * ${NPTS} + j + 1], aPanel) * uPxPerUnit;
    float W = i == 0 ? uW[0] : (i == 1 ? uW[1] : uW[2]);
    float hc = ${CORE_HALF_PX.toFixed(2)} * W;
    // R1: a core thinner than one SCREEN pixel keeps one pixel and loses light;
    // below full resolution a plateau of half a buffer pixel stays inside the
    // ramp, or the sampled peak swings with the sub-pixel offset
    vGain = min(1.0, hc / 0.5);
    hc = max(hc, mix(0.5, vAa + 0.5 * uBufPx, lowRes));
    float hs = max(hc + ${SHEATH_PX.toFixed(2)} * W, hc + uBufPx);
    vHc = hc; vHs = hs;
    vec2 d = b - a;
    float L = length(d);
    vec2 t = L > 1e-4 ? d / L : vec2(1.0, 0.0);
    vec2 nrm = vec2(-t.y, t.x);
    float R = hs + ${HALO_REACH_PX.toFixed(1)} + uBufPx;
    vec2 p = (a + b) * 0.5 + t * position.x * (L * 0.5 + R) + nrm * position.y * R;
    vRel = p - a;
    vSeg = d;
    vCol = i == 0 ? uCol[0] : (i == 1 ? uCol[1] : uCol[2]);
    gl_Position = vec4(p.x / uPxPerUnit / uAspect, p.y / uPxPerUnit, 0.0, 1.0);
  }
`;

// The distance to the segment, per pixel: a capsule, so consecutive
// sub-segments join round, and MAX blending joins them without beads.
const segmentFragmentShader = `
  precision mediump float;
  varying float vAa;
  varying vec2 vRel;
  varying vec2 vSeg;
  varying float vHc;
  varying float vHs;
  varying float vGain;
  varying vec3 vCol;
  void main() {
    float h = clamp(dot(vRel, vSeg) / max(dot(vSeg, vSeg), 1e-6), 0.0, 1.0);
    float d = length(vRel - vSeg * h);
    float core = (1.0 - smoothstep(vHc - vAa, vHc + vAa, d)) * vGain;
    float sheath = 1.0 - smoothstep(vHs - vAa, vHs + vAa, d);
    float halo = 0.35 * exp(-max(d - vHs, 0.0) / 10.0);
    float e = max(core, ${SHEATH_E.toFixed(2)} * max(sheath, halo));
    gl_FragColor = vec4(vCol * e, e);
  }
`;

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// THE POSE: exposure x keep - floor, the colour scaled with it (the hue
// stays); the shutter's band to 0; in RGBA8 a stochastic rounding.
const decayFragmentShader = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
  uniform sampler2D uPrev;
  uniform float uKeep;
  uniform float uSub;
  uniform float uQuant;
  uniform float uSeed;
  uniform vec2 uWipe;        // the band swept since the last frame, in uv.x
  varying vec2 vUv;
  void main() {
    vec4 p = texture2D(uPrev, vUv);
    float a = max(p.a * uKeep - uSub, 0.0);
    vec4 x = vec4(p.rgb * (p.a > 1e-5 ? a / p.a : 0.0), a);
    if (uQuant > 0.5) {
      vec2 fc = mod(floor(gl_FragCoord.xy) + vec2(uSeed, uSeed * 1.618), 64.0);
      float r = fract(dot(fc, vec2(0.7548776662, 0.5698402909)) + uSeed * 0.381966);
      x = floor(x * 255.0 + r) / 255.0;
    }
    x = max(x, 0.0);
    float band = step(uWipe.x, vUv.x) * (1.0 - step(uWipe.y, vUv.x));
    gl_FragColor = x * (1.0 - band);
  }
`;

const displayFragmentShader = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
  uniform sampler2D uBuf;
  uniform vec2 uTexSize;
  uniform float uManual;     // 1: a float buffer below full resolution (no hardware filtering on the Pi)
  uniform float uView;       // 1: the raw exposure (debug)
  varying vec2 vUv;
  vec4 fetch(vec2 uv) {
    if (uManual < 0.5) return texture2D(uBuf, uv);
    vec2 p = uv * uTexSize - 0.5;
    vec2 f = fract(p);
    vec2 b = (floor(p) + 0.5) / uTexSize, dx = vec2(1.0 / uTexSize.x, 0.0), dy = vec2(0.0, 1.0 / uTexSize.y);
    return mix(mix(texture2D(uBuf, b), texture2D(uBuf, b + dx), f.x),
               mix(texture2D(uBuf, b + dy), texture2D(uBuf, b + dx + dy), f.x), f.y);
  }
  void main() {
    vec4 s = fetch(vUv);
    float e = min(s.a, 1.0);
    if (uView > 0.5) { gl_FragColor = vec4(vec3(e), 1.0); return; }
    vec3 c = min(s.rgb / max(s.a, 1e-4), vec3(1.0));
    // a toe on the last steps: the tail fades instead of popping off
    vec3 col = c * min(e / ${SHEATH_E.toFixed(2)}, 1.0) * smoothstep(1.5 / 255.0, 10.0 / 255.0, e);
    float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
    col *= min(1.0, ${(SHEATH_LUM + 0.04).toFixed(2)} / max(l, 1e-4));
    float w = smoothstep(${WHITE_FROM.toFixed(2)}, ${WHITE_TO.toFixed(2)}, e);
    col = mix(col, vec3(${WARM_WHITE.join(', ')}), w);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const starVertexShader = `
  precision highp float;
  ${PANEL_GLSL}
  attribute float aSrc;
  attribute float aPanel;
  uniform vec2 uHead[3];
  uniform float uHeadOn[3];
  uniform vec3 uHeadCol[3];
  uniform float uAspect;
  uniform float uPxPerUnit;
  uniform float uSpike;
  uniform float uPulse;
  varying vec2 vLoc;
  varying vec3 vCol;
  varying float vSpike;
  void main() {
    int i = int(aSrc + 0.5);
    float on = i == 0 ? uHeadOn[0] : (i == 1 ? uHeadOn[1] : uHeadOn[2]);
    if (on < 0.5 || aPanel >= uPanels) { gl_Position = vec4(0.0); return; }
    vec2 q = i == 0 ? uHead[0] : (i == 1 ? uHead[1] : uHead[2]);
    vec2 head = toScreen(q, aPanel) * uPxPerUnit;
    float sp = uSpike * (i < 2 ? 1.0 + ${BEAT_WIDEN.toFixed(2)} * uPulse : 0.7);
    float R = 4.0 * sp + 2.0;
    vLoc = position.xy * R;
    vSpike = sp;
    vCol = i == 0 ? uHeadCol[0] : (i == 1 ? uHeadCol[1] : uHeadCol[2]);
    vec2 p = head + vLoc;
    gl_Position = vec4(p.x / uPxPerUnit / uAspect, p.y / uPxPerUnit, 0.0, 1.0);
  }
`;

// The source, drawn on the SCREEN after the buffer (never into it): a very
// bright point, a small glow and a 4-branch diffraction star of 1-px lines
// with a triangle filter (constant energy whatever the sub-pixel offset).
const starFragmentShader = `
  precision mediump float;
  varying vec2 vLoc;
  varying vec3 vCol;
  varying float vSpike;
  void main() {
    vec2 q = vLoc;
    float r2 = dot(q, q);
    float core = exp(-r2 / (2.0 * 1.3 * 1.3));
    float glow = 0.18 * exp(-sqrt(r2) / 4.0);
    float bx = exp(-abs(q.x) / vSpike) * max(0.0, 1.0 - abs(q.y));
    float by = exp(-abs(q.y) / vSpike) * max(0.0, 1.0 - abs(q.x));
    float hx = exp(-abs(q.x) / 7.0), hy = exp(-abs(q.y) / 7.0);
    vec3 warm = vec3(${WARM_WHITE.join(', ')});
    vec3 spikes = mix(vCol, warm, hx) * bx + mix(vCol, warm, hy) * by;
    vec3 c = warm * core * 1.3 + vCol * glow + spikes * 0.85;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// quads: source x panel x sub-segment (stars: source x panel)
function quadGeometry(nSub) {
  const nq = 3 * 2 * nSub;
  const pos = new Float32Array(nq * 4 * 3), src = new Float32Array(nq * 4), pan = new Float32Array(nq * 4), sub = new Float32Array(nq * 4);
  const idx = [];
  const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
  let q = 0;
  for (let i = 0; i < 3; i++) for (let p = 0; p < 2; p++) for (let j = 0; j < nSub; j++, q++) {
    for (let c = 0; c < 4; c++) {
      const v = q * 4 + c;
      pos[v * 3] = corners[c][0]; pos[v * 3 + 1] = corners[c][1];
      src[v] = i; pan[v] = p; sub[v] = j;
    }
    idx.push(q * 4, q * 4 + 1, q * 4 + 2, q * 4, q * 4 + 2, q * 4 + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('aSrc', new THREE.BufferAttribute(src, 1));
  g.setAttribute('aPanel', new THREE.BufferAttribute(pan, 1));
  g.setAttribute('aSub', new THREE.BufferAttribute(sub, 1));
  g.setIndex(idx);
  return g;
}

// --- l'extraction -------------------------------------------------------------
// ONE worker for the whole page, never terminated: it warms its JIT once
// (a cold extraction took 0.5 to 0.9 s on the Pi) and every appearance of
// the visualizer reuses it. Replies are routed by request id.
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function sharedWorker() {
  if (_worker || _workerFailed) return _worker;
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') { _workerFailed = true; return null; }
  try {
    const url = URL.createObjectURL(new Blob([contourWorkerSource()], { type: 'text/javascript' }));
    _worker = new Worker(url);
    _worker.onmessage = (ev) => {
      const cb = _waiting.get(ev.data.id);
      if (cb) { _waiting.delete(ev.data.id); cb(ev.data); }
    };
    _worker.onerror = () => {
      // a broken worker: every pending request falls back to the main thread
      for (const [id, cb] of _waiting) cb({ id, error: 'worker' });
      _waiting.clear();
    };
  } catch (e) { _workerFailed = true; _worker = null; }
  return _worker;
}
function askWorker(msg, cb) {
  const w = sharedWorker();
  if (!w) return false;
  _waiting.set(msg.id, cb);
  w.postMessage(msg);
  return true;
}
// The pixels on the main thread (no OffscreenCanvas in the worker): a 192 px
// canvas, a few ms.
function decodeOnMainThread(url, n, done, fail) {
  if (typeof Image === 'undefined' || typeof document === 'undefined') { fail('nodom'); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const c = document.createElement('canvas');
      c.width = n; c.height = n;
      const g = c.getContext('2d');
      g.drawImage(img, 0, 0, n, n);
      done(g.getImageData(0, 0, n, n).data);
    } catch (e) { fail(String(e)); }
  };
  img.onerror = () => fail('img');
  img.src = url;
}

export class LightpaintVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._layoutTier = TIER[this._tier];
    const st = (urlParam('lpstyle') || '').toLowerCase();
    this._forceStyle = STYLES.includes(st) ? st : null;
    const d = parseFloat(urlParam('lpdecay'));
    this._forceHalfLife = Number.isFinite(d) && d > 0 ? d : null;
    this._forceDrop = urlParam('lpdrop') === '1';
    this._forceRgba8 = urlParam('lpbuffer') === 'rgba8';
    this._viewExposure = urlParam('lpview') === 'exposure';
    this._debugContours = urlParam('lpcontours') === '1';
    this._debugPoster = urlParam('lpposter') === '1';
    // NO SHARED BLOOM: its widest level veiled half the frame. The trace's
    // halo (R1) and the star's glow are drawn instead.
    this._post = {
      bloomStrength: 0, bloomThreshold: 0.78, grain: 0, vignette: 0.30,
      aberration: 0.0012, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1
    };
    if (urlParam('lpprobe') === '1' && typeof window !== 'undefined') window.__lightpaint = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    this._buffer = null;
    sharedWorker();   // warming starts now, before the first cover is asked

    const v2s = (n) => Array.from({ length: n }, () => new THREE.Vector2());
    const cols = () => [new THREE.Color(), new THREE.Color(), new THREE.Color()];
    this._panelU = {
      uPanelC: { value: v2s(2) }, uPanelH: { value: 0.5 }, uMirror: { value: new THREE.Vector2(-1, 1) }, uPanels: { value: 2 },
      uAspect: { value: 1.78 }, uPxPerUnit: { value: 540 }
    };
    const pu = this._panelU;

    // 1. the pose: previous -> write, decayed, shutter band cleared
    this._decayU = {
      uPrev: { value: null }, uKeep: { value: 1 }, uSub: { value: 0 }, uQuant: { value: 0 },
      uSeed: { value: 0 }, uWipe: { value: new THREE.Vector2(2, 2) }
    };
    this._decayQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._decayU, vertexShader: quadVertexShader, fragmentShader: decayFragmentShader,
      depthTest: false, depthWrite: false
    }));
    this._decayQuad.frustumCulled = false;
    this._decayScene = new THREE.Scene();
    this._decayScene.add(this._decayQuad);

    // 2. the strokes, MAX-blended into the same target
    this._segU = Object.assign({}, pu, {
      uPts: { value: v2s(3 * NPTS) }, uN: { value: [0, 0, 0] }, uCol: { value: cols() },
      uW: { value: [1, 1, FRAME_W] }, uBufPx: { value: 1 / this._layoutTier.scale }
    });
    this._segMat = new THREE.ShaderMaterial({
      uniforms: this._segU, vertexShader: segmentVertexShader, fragmentShader: segmentFragmentShader,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation,
      blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
    });
    this._segMesh = new THREE.Mesh(quadGeometry(SUB), this._segMat);
    this._segMesh.frustumCulled = false;
    this._segScene = new THREE.Scene();
    this._segScene.add(this._segMesh);
    this._privCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // 3. the display, in the main scene
    this._dispU = {
      uBuf: { value: null }, uTexSize: { value: new THREE.Vector2(1, 1) }, uManual: { value: 0 },
      uView: { value: this._viewExposure ? 1 : 0 }
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader,
      depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // 4. the heads, additive, on the screen after the display
    this._starU = Object.assign({}, pu, {
      uHead: { value: v2s(3) }, uHeadOn: { value: [0, 0, 0] }, uHeadCol: { value: cols() },
      uSpike: { value: SPIKE_PX }, uPulse: { value: 0 }
    });
    this._stars = new THREE.Mesh(quadGeometry(1), new THREE.ShaderMaterial({
      uniforms: this._starU, vertexShader: starVertexShader, fragmentShader: starFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    }));
    this._stars.frustumCulled = false;
    this._stars.renderOrder = -999;
    this._stars.visible = !this._viewExposure;
    scene.add(this._stars);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._clock = 0; this._clockPrev = 0;
    this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._phraseBeats = null; this._phraseRate = null;
    this._hasGrid = false;
    this._bpm = NO_BPM;
    this._lastBeatFloor = 0;
    this._sinceBeat = 10;
    this._plan = null;
    this._draw = [{ k: 0, s: 0 }, { k: 0, s: 0 }, { k: 0, s: 0 }];
    this._photoNo = 0;
    this._styleIdx = 0;
    this._palette = null;
    this._cols = styleColours(null);
    this._paletteSeen = undefined;
    this._coverUrl = undefined;
    this._data = null;
    this._req = null;
    this._wipe = null;
    this._wipeDone = null;
    this._clearOnce = false;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._layout = null; this._layoutKey = '';
    this._screenH = 1080;
    this._floorAcc = 0;
    this._frameNo = 0;
    this._log = [];
    this._pts = new Float32Array(2 * NPTS);
    this._debugCanvas = null;
    this._debugDirty = true;
  }

  _note(what, extra) {
    this._log.push(Object.assign({ t: +this._time.toFixed(3), what, beat: +this._clock.toFixed(3) }, extra || {}));
    if (this._log.length > 400) this._log.shift();
  }

  // --- the cover ------------------------------------------------------------------
  _request(url) {
    const id = ++_nextId;
    if (!url) { this._req = null; this._receive(rosaceData(), 'no-cover'); return; }
    let abs = artProxyUrl(url);
    try { abs = new URL(abs, typeof location !== 'undefined' ? location.href : undefined).href; } catch (e) { /* keep it */ }
    this._req = { id, url: abs, t: this._time };
    if (!askWorker({ id, url: abs, n: CONTOUR_N }, (m) => this._onWorker(m))) this._fallbackDecode(this._req);
  }
  // no OffscreenCanvas in the worker, or no worker: decode here, extract in
  // the worker when there is one, else here (the brief's 150 ms then show)
  _fallbackDecode(req) {
    decodeOnMainThread(req.url, CONTOUR_N, (rgba) => {
      if (!this._req || this._req.id !== req.id) return;
      const sent = !_workerFailed && askWorker({ id: req.id, rgba, n: CONTOUR_N }, (m) => (m.error ? this._extractHere(req, rgba) : this._onWorker(m)));
      if (!sent) this._extractHere(req, rgba);
    }, (why) => {
      if (!this._req || this._req.id !== req.id) return;
      this._req = null;
      this._receive(rosaceData(), 'decode-' + why);
    });
  }
  _extractHere(req, rgba) {
    if (!this._req || this._req.id !== req.id) return;
    const t0 = performance.now();
    const res = extractContours(rgba, CONTOUR_N);
    res.ms = performance.now() - t0;
    this._onWorker({ id: req.id, res });
  }
  _onWorker(m) {
    if (!this._scene || !this._req || m.id !== this._req.id) return;
    if (m.error) {
      if (!m.res && (m.error === 'nooffscreen' || m.error === 'worker' || /http|fetch|network|decode|image/i.test(m.error))) { this._fallbackDecode(this._req); return; }
      this._req = null;
      this._receive(rosaceData(), 'worker-' + m.error);
      return;
    }
    this._req = null;
    const r = m.res;
    this._note('contours', { ms: r.ms != null ? +r.ms.toFixed(1) : null, decodeMs: r.decodeMs != null ? +r.decodeMs.toFixed(1) : null, strokes: r.strokes.length, fallback: r.fallback });
    this._lastExtraction = r;
    this._receive(r.fallback ? Object.assign(rosaceData(), { poster: r.poster }) : r, 'cover');
  }
  _receive(data, why) {
    this._data = data;
    this._debugDirty = true;
    this._note('data', { why, rosace: !!data.rosace });
    if (!this._plan) this._newPhoto(this._clock, false);
    else if (!this._wipe) this._startShutter(this._clock, this._clock + 1, 'cover');
  }

  // --- the photo ------------------------------------------------------------------
  // the clock beat where the photo ends (the shutter's last beat ends it)
  _planEnd(c0, drop, ph) {
    const Pb = this._phraseBeats;
    const phrase = this._hasGrid && Pb && ph != null;
    if (drop) {
      if (phrase) { let e = c0 + (1 - ph) * Pb; while (e - c0 < 12) e += Pb; return e; }
      return c0 + 16;
    }
    if (!this._hasGrid) return c0 + NO_GRID_PHOTO_BEATS;
    if (phrase) {
      let left = (1 - ph) * Pb;
      if (left < 2) left += Pb;
      let e = c0 + left;
      while (e - c0 < MIN_PHOTO_BEATS - 0.5) e += Pb;
      return e;
    }
    return c0 + MIN_PHOTO_BEATS;
  }

  _newPhoto(c0, drop) {
    if (!this._data) return;
    const data = this._data;
    if (!drop) {
      this._photoNo++;
      this._styleIdx++;
    }
    const style = this._forceStyle || STYLES[this._styleIdx % STYLES.length];
    const end = this._planEnd(c0, drop, this._phasePhrase);
    const D = drop ? DROP_DRAW_BEATS
      : (this._hasGrid ? Math.max(8, Math.min(DRAW_BEATS, end - c0 - HOLD_BEATS)) : NO_GRID_DRAW_BEATS);
    const src = [];
    for (let s = 0; s < 2; s++) {
      const list = data.strokes.filter((st) => st.src === s);
      src.push(Object.assign(scheduleSource(list, D), { data }));
    }
    if (!drop) src.push(Object.assign(scheduleSource(FRAME_DATA.strokes, Math.max(8, end - c0 - 1.5)), { data: FRAME_DATA }));
    else src.push({ strokes: [], speed: 0, data: FRAME_DATA });
    this._plan = { data, style, mirror: !drop && this._photoNo % 2 === 1, drop, c0, end, D, src };
    for (const d of this._draw) { d.k = 0; d.s = 0; }
    this._colourPlan();
    this._layoutKey = '';
    this._debugDirty = true;
    this._note(drop ? 'drop' : 'photo', { style, mirror: this._plan.mirror, end: +end.toFixed(2), D, strokes: data.strokes.length, rosace: !!data.rosace });
  }
  _colourPlan() {
    const P = this._plan;
    if (!P) return;
    for (let i = 0; i < 3; i++) for (const st of P.src[i].strokes) {
      st.col = st.col || new THREE.Color();
      if (i === 2) st.col.copy(P.style === 'neon' ? this._cols[2] : this._cols[0]);
      else strokeColour(P.style, st, this._cols, st.col);
    }
  }

  // THE DROP: everything cleared, one big drawing traced in 2 bars
  _startDrop() {
    this._wipe = null; this._wipeDone = null;
    this._clearOnce = true;
    this._newPhoto(this._clock, true);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // --- the shutter -----------------------------------------------------------------
  _startShutter(c0, c1, why) {
    const dir = this._photoNo % 2 === 0 ? -1 : 1;
    this._wipe = { c0, c1: Math.max(c1, c0 + 0.25), dir, prev: 0 };
    this._note('shutter', { why, until: +this._wipe.c1.toFixed(2) });
  }
  _tickShutter() {
    const W = this._wipe, D = this._decayU;
    if (!W) { D.uWipe.value.set(2, 2); return; }
    const f = clamp01((this._clock - W.c0) / (W.c1 - W.c0));
    const lo = W.prev, hi = f >= 1 ? 1.01 : f;
    if (W.dir > 0) D.uWipe.value.set(lo <= 0 ? -0.01 : lo, hi);
    else D.uWipe.value.set(1 - hi, lo <= 0 ? 1.01 : 1 - lo);
    W.prev = hi;
    if (f >= 1) { this._wipe = null; this._wipeDone = W.c1; }
  }

  // --- the layout -------------------------------------------------------------------
  _updateLayout() {
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    // R19: the panels are laid out around the centre of composition (the
    // cover block as it would sit centred), not the cover's position.
    const c = safeZoneUniforms.uStageCenter.value, h = safeZoneUniforms.uStageHalfSize.value;
    const drop = !!(this._plan && this._plan.drop);
    const mirror = !!(this._plan && this._plan.mirror);
    const key = [aspect, c.x, c.y, h.x, h.y, this._screenH].map((v) => v.toFixed(3)).join(',') + (drop ? 'D' : 'd') + (mirror ? 'M' : 'm');
    if (key === this._layoutKey) return;
    this._layoutKey = key;
    const L = layoutPanels(aspect, [c.x, c.y], [h.x, h.y], this._screenH, drop);
    this._layout = L;
    const pu = this._panelU;
    pu.uAspect.value = aspect;
    pu.uPanelH.value = L.h;
    pu.uPanels.value = L.centres.length;
    pu.uPanelC.value[0].set(L.centres[0][0], L.centres[0][1]);
    const c1 = L.centres[1] || L.centres[0];
    pu.uPanelC.value[1].set(c1[0], c1[1]);
    if (!mirror) pu.uMirror.value.set(1, 1);
    else if (L.axis === 'x') pu.uMirror.value.set(-1, 1);
    else pu.uMirror.value.set(1, -1);
    this._debugDirty = true;
  }

  // --- the strokes of this frame ---------------------------------------------------
  _advance(pulse) {
    const P = this._plan, U = this._segU, S = this._starU;
    for (let i = 0; i < 3; i++) { U.uN.value[i] = 0; S.uHeadOn.value[i] = 0; }
    if (!P) return;
    const t = this._clock - P.c0;
    const out = this._pts;
    for (let i = 0; i < 3; i++) {
      const src = P.src[i], list = src.strokes, n = list.length;
      if (!n) continue;
      const st = this._draw[i];
      // where the lamp should be now
      let kt = Math.min(st.k, n - 1);
      while (kt + 1 < n && t >= list[kt + 1].t0) kt++;
      const sT = clamp01((t - list[kt].t0) / Math.max(1e-6, list[kt].t1 - list[kt].t0)) * list[kt].len;
      // where it has drawn up to: never across two strokes in one frame
      while (st.k < kt && st.s >= list[st.k].len - 1e-9) { st.k++; st.s = 0; }
      const k = st.k, s0 = st.s;
      const s1 = k < kt ? list[k].len : Math.max(s0, sT);
      if (s1 > s0 + 1e-7) {
        const np = strokePortion(src.data, list[k], s0, s1, out);
        for (let j = 0; j < NPTS; j++) {
          const jj = Math.min(j, np - 1);
          U.uPts.value[i * NPTS + j].set(out[2 * jj], out[2 * jj + 1]);
        }
        U.uN.value[i] = np - 1;
        st.s = s1;
        U.uCol.value[i].copy(list[k].col);
      }
      // the head: on while its lamp is on (not between strokes, not done)
      const lampOn = t >= list[kt].t0 && t <= list[kt].t1 + 1e-6 && t >= 0;
      if (lampOn || st.k < kt) {
        pointAt(src.data, list[st.k], st.s, out);
        S.uHead.value[i].set(out[0], out[1]);
        S.uHeadOn.value[i] = 1;
        S.uHeadCol.value[i].copy(list[st.k].col);
      }
    }
    U.uW.value[0] = 1 + BEAT_WIDEN * pulse;
    U.uW.value[1] = 1 + BEAT_WIDEN * pulse;
    U.uW.value[2] = FRAME_W;
    S.uPulse.value = pulse;
  }

  // --- the frame -----------------------------------------------------------------
  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._paused = profile.playbackState !== 'PLAYING';
    if (profile.artworkPalette !== this._paletteSeen) {
      this._paletteSeen = profile.artworkPalette;
      this._cols = styleColours(profile.artworkPalette);
      this._colourPlan();
    }
    this._updateLayout();
    if (this._paused) {
      // frozen: nothing decays, nothing draws, the heads stay where they are
      for (let i = 0; i < 3; i++) this._segU.uN.value[i] = 0;
      this._drawDebug();
      return;
    }
    this._time += dt;

    // THE CLOCK, in beats: integrated at the tempo, pulled softly toward the
    // grid with a whole number of bars of offset.
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    this._hasGrid = bp != null && bpm > 0;
    this._bpm = this._hasGrid ? bpm : NO_BPM;
    this._clockPrev = this._clock;
    let jump = 0;
    if (this._hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) {
        // a seek: the clock jumps, the photo is rebased so the drawing goes on
        this._targetOffset = 4 * Math.round((this._clock - target) / 4);
        const before = this._clock;
        this._clock = target + this._targetOffset;
        jump = this._clock - before;
      } else {
        const rate = this._bpm / 60;
        this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5));
      }
    } else {
      this._clock += dt * this._bpm / 60;
      this._targetOffset = null;
    }
    if (jump !== 0) {
      if (this._plan) { this._plan.c0 += jump; this._plan.end += jump; }
      if (this._wipe) { this._wipe.c0 += jump; this._wipe.c1 += jump; }
    }
    const dClock = this._clock - this._clockPrev;
    // the director's phrase: its length in beats (measured)
    if (this._hasGrid && ph != null) {
      if (this._phasePhrase != null && ph >= this._phasePhrase - 0.5 && dClock > 1e-4 && jump === 0) {
        const r = (ph - this._phasePhrase) / dClock;
        if (r > 0) this._phraseRate = this._phraseRate == null ? r : this._phraseRate + (r - this._phraseRate) * 0.05;
      }
      this._phasePhrase = ph;
      if (this._phraseRate) this._phraseBeats = Math.max(4, 4 * Math.round(1 / this._phraseRate / 4));
    } else { this._phasePhrase = null; this._phraseBeats = null; }
    // the photo's end follows the director's phrase when they are close
    const P = this._plan;
    if (P && this._phraseBeats && ph != null && !this._wipe) {
      const pe = this._clock + (1 - ph) * this._phraseBeats;
      if (Math.abs(pe - P.end) < 2.5) P.end = pe;
    }

    // the cover: after the clock, so the first photo is planned on the grid
    const url = profile.artworkUrl || null;
    if (url !== this._coverUrl) { this._coverUrl = url; this._request(url); }
    if (this._req && this._time - this._req.t > COVER_TIMEOUT_S) {
      this._req = null;
      this._receive(rosaceData(), 'timeout');
    }

    const beatF = Math.floor(this._clock);
    if (beatF !== this._lastBeatFloor) { this._sinceBeat = 0; this._lastBeatFloor = beatF; }
    else this._sinceBeat += dt;
    const pulse = this._hasGrid ? 1 - smooth01((this._sinceBeat - (PULSE_S - 0.03)) / 0.03) : 0;

    // THE SHUTTER, in the last beat of the photo
    if (this._plan && !this._wipe && this._clock >= this._plan.end - 1) this._startShutter(this._clock, this._plan.end, this._plan.drop ? 'drop-end' : 'phrase');
    this._tickShutter();
    if (this._wipeDone != null) { this._newPhoto(this._wipeDone, false); this._wipeDone = null; }

    // DROP: the analysis' marks, or ?lpdrop=1 (every 8 bars)
    let drop = this._hasGrid && this._tickDrops(profile);
    const barF = Math.floor(this._clock / 4);
    if (this._forceDrop && Math.floor(this._clockPrev / 4) !== barF && ((barF % 8) + 8) % 8 === 1) drop = true;
    if (drop && this._data && !(this._plan && this._plan.drop)) this._startDrop();

    this._updateLayout();
    this._advance(pulse);

    // THE POSE: a half-life in bars, whatever the frame rate
    const barS = 4 * 60 / this._bpm;
    const hlBars = this._forceHalfLife != null ? this._forceHalfLife : (this._plan && this._plan.drop ? HALF_LIFE_DROP_BARS : HALF_LIFE_BARS);
    this._decayU.uKeep.value = Math.pow(0.5, dt / (hlBars * barS));
    this._floorAcc += LINEAR_FLOOR_PER_S * dt;
    this._drawDebug();
  }

  // --- debug: the contours / the posterised cover, on a DOM canvas ---------------
  _drawDebug() {
    if (!(this._debugContours || this._debugPoster) || typeof document === 'undefined') return;
    if (!this._debugDirty || !this._layout) return;
    this._debugDirty = false;
    let cv = this._debugCanvas;
    if (!cv) {
      cv = this._debugCanvas = document.createElement('canvas');
      cv.style.cssText = 'position:fixed;left:0;top:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483000';
      document.body.appendChild(cv);
    }
    const W = Math.round(window.innerWidth * (window.devicePixelRatio || 1)), H = Math.round(window.innerHeight * (window.devicePixelRatio || 1));
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
    const g = cv.getContext('2d');
    g.clearRect(0, 0, W, H);
    const L = this._layout, A = this._panelU.uAspect.value, m1 = this._panelU.uMirror.value;
    const toPx = (q, p) => {
      const m = p === 1 ? m1 : { x: 1, y: 1 };
      const c = L.centres[p];
      const X = c[0] + L.h * q[0] * m.x, Y = c[1] + L.h * q[1] * m.y;
      return [(X / A * 0.5 + 0.5) * W, (0.5 - Y * 0.5) * H];
    };
    const d = this._data;
    for (let p = 0; p < L.centres.length; p++) {
      if (this._debugPoster && d && d.poster) {
        const N = CONTOUR_N, img = g.createImageData(N, N);
        for (let i = 0; i < N * N; i++) { const v = d.poster[i] * 85; img.data[4 * i] = v; img.data[4 * i + 1] = v; img.data[4 * i + 2] = v; img.data[4 * i + 3] = 150; }
        const tmp = document.createElement('canvas'); tmp.width = N; tmp.height = N; tmp.getContext('2d').putImageData(img, 0, 0);
        const a = toPx([-1, 1], p), b = toPx([1, -1], p);
        g.save(); g.translate(a[0], a[1]); g.scale((b[0] - a[0]) / N, (b[1] - a[1]) / N); g.drawImage(tmp, 0, 0); g.restore();
      }
      if (this._debugContours && d) {
        g.lineWidth = 1.5;
        g.font = Math.round(H / 60) + 'px sans-serif';
        for (const st of d.strokes) {
          g.strokeStyle = st.src === 0 ? '#00e5ff' : '#ffd400';
          g.beginPath();
          for (let j = 0; j < st.count; j++) {
            const i = st.start + j, q = toPx([d.pts[4 * i], d.pts[4 * i + 1]], p);
            if (j === 0) g.moveTo(q[0], q[1]); else g.lineTo(q[0], q[1]);
          }
          g.stroke();
          const q0 = toPx([d.pts[4 * st.start], d.pts[4 * st.start + 1]], p);
          g.fillStyle = '#fff';
          g.fillText(String(st.order), q0[0] + 3, q0[1] - 3);
        }
      }
    }
  }

  _ensureBuffer(renderer) {
    if (this._buffer) return;
    const ext = renderer.extensions;
    const isGL2 = renderer.capabilities.isWebGL2;
    const floatOk = !this._forceRgba8 && (isGL2
      ? (ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float'))
      : (ext.has('OES_texture_half_float') && ext.has('EXT_color_buffer_half_float')));
    this._float = floatOk;
    const h = renderer.getDrawingBufferSize(_size).y;
    const sc = this._layoutTier.scale;
    const scale = sc >= 1 ? 1 : Math.max(sc, Math.min(1, MIN_BUFFER_H / Math.max(1, h)));
    this._buffer = new FeedbackBuffer({
      scale,
      type: floatOk ? THREE.HalfFloatType : THREE.UnsignedByteType,
      // the Pi renders to float targets but cannot FILTER them
      filter: floatOk ? THREE.NearestFilter : THREE.LinearFilter
    });
    this._decayU.uQuant.value = floatOk ? 0 : 1;
    if (!(isGL2 || ext.has('EXT_blend_minmax'))) {
      this._segMat.blendEquation = THREE.AddEquation;
      this._segMat.blendSrc = THREE.OneMinusDstColorFactor;
      this._segMat.blendDst = THREE.OneFactor;
      this._segMat.needsUpdate = true;
    }
    this._note('buffer', { float: floatOk, scale });
  }

  _render(renderer) {
    const size = renderer.getDrawingBufferSize(_size);
    if (Math.abs(size.y - this._screenH) > 0.5) { this._screenH = size.y; this._updateLayout(); }
    this._ensureBuffer(renderer);
    const buf = this._buffer;
    // FeedbackBuffer clears to ALPHA 1, and alpha is the exposure here: a
    // new pair must be cleared to 0 or the first frames are white
    if (buf.ensureSize(renderer)) {
      const prevT = renderer.getRenderTarget(), pc = renderer.getClearColor(new THREE.Color()), pa = renderer.getClearAlpha();
      renderer.setClearColor(0x000000, 0);
      for (const t of [buf.read, buf.write]) { renderer.setRenderTarget(t); renderer.clear(true, false, false); }
      renderer.setRenderTarget(prevT);
      renderer.setClearColor(pc, pa);
    }
    this._panelU.uPxPerUnit.value = size.y / 2;
    this._segU.uBufPx.value = size.y / Math.max(1, buf.height);
    this._dispU.uTexSize.value.set(buf.width, buf.height);
    this._dispU.uManual.value = this._float && buf.width < size.x - 0.5 ? 1 : 0;
    this._dispU.uBuf.value = buf.read.texture;
    if (this._paused) return;
    let sub;
    if (this._float) { sub = this._floorAcc; this._floorAcc = 0; }
    else { sub = 0; if (this._floorAcc >= 1 / 255) { sub = 1 / 255; this._floorAcc -= 1 / 255; } }
    this._decayU.uSub.value = sub;
    this._decayU.uSeed.value = (this._frameNo++ * 7) % 64;
    if (this._clearOnce) { this._decayU.uWipe.value.set(-0.01, 1.01); this._clearOnce = false; }
    const prev = renderer.getRenderTarget();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    this._decayU.uPrev.value = buf.read.texture;
    renderer.setRenderTarget(buf.write);
    renderer.render(this._decayScene, this._privCam);
    renderer.render(this._segScene, this._privCam);
    renderer.setRenderTarget(prev);
    renderer.autoClear = autoClear;
    buf.swap();
    this._dispU.uBuf.value = buf.read.texture;
    // this frame's strokes are in the buffer: never paint them twice
    for (let i = 0; i < 3; i++) this._segU.uN.value[i] = 0;
  }

  dispose() {
    if (this._scene) {
      if (this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
      if (this._stars) this._scene.remove(this._stars);
    }
    for (const m of [this._display, this._stars, this._decayQuad, this._segMesh]) {
      if (m) { m.geometry.dispose(); m.material.dispose(); }
    }
    if (this._buffer) this._buffer.dispose();
    // the shared worker stays (warm for the next appearance); a reply still
    // in flight finds no request and is dropped
    this._req = null;
    if (this._debugCanvas && this._debugCanvas.parentNode) this._debugCanvas.parentNode.removeChild(this._debugCanvas);
    this._debugCanvas = null;
    if (typeof window !== 'undefined' && window.__lightpaint === this) window.__lightpaint = null;
    this._scene = null;
  }
}

function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
const _size = new THREE.Vector2();
